import React from 'react';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';

import type { Maybe } from 'graphql/jsutils/Maybe';

import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';
import MediumComponent from 'src/components/other/MediumComponent';

import type { Medium } from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const PreviewCard = styledComponent(Card)`
    background-color: ${colours.primary.O04};
`;

PreviewCard.defaultProps = {
    elevation: 0,
};

interface PreviewCardActionAreaProps {
    component?: string;
}

const PreviewCardActionArea = styledComponent(CardActionArea)<PreviewCardActionAreaProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: stretch;
    position: relative;
    img,
    video {
        width: 100%;
        max-height: 100%;
        min-height: 100%;
        object-fit: cover;
    }
`;

const PreviewCardContent = styledComponent(CardContent)`
    flex: 1;
    width: 100%;
    padding: 0.5rem 1rem;
`;

const CardTitle = styledComponent(Typography)`
    color: ${colours.black.O87};
`;

const CardDescription = styledComponent(Typography)`
    color: ${colours.black.O72};
`;

const IconArea = styledComponent(Box)`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    align-items: center;
`;

interface Props {
    media?: Maybe<Medium>;
    title?: string | null;
    subtitle?: string;
    onClick: () => void;
    iconArea?: React.ReactElement | React.ReactElement[];
}

const ProjectCard = ({ media, title, subtitle, onClick, iconArea }: Props) => {
    const { logEventDynamic } = useDynamicLogEvents();
    return (
        <PreviewCard
            onClick={() => {
                logEventDynamic(AnalyticsEvent.SeeProject);
                onClick();
            }}
        >
            <PreviewCardActionArea component="div">
                <MediumComponent cropWidth={192} cropHeight={210} media={media} />
                <PreviewCardContent>
                    <CardTitle variant="h6" noWrap>
                        {title}
                    </CardTitle>
                    <CardDescription variant="subtitle2">{subtitle}</CardDescription>
                </PreviewCardContent>
                {iconArea && <IconArea>{iconArea}</IconArea>}
            </PreviewCardActionArea>
        </PreviewCard>
    );
};

export default ProjectCard;
